import { useEffect, useState } from "react";
import { api, euro, fmtDate } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/common";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { UsersIcon as Users, FileTextIcon as FileText, PrinterIcon as Printer, CurrencyEurIcon as CurrencyEur, WarningIcon as Warning, ClockCounterClockwiseIcon as ClockCounterClockwise, ShieldCheckIcon as ShieldCheck, StorefrontIcon as Storefront, GlobeIcon as Globe, WifiHighIcon as WifiHigh, LockIcon as Lock, CalendarCheckIcon as CalendarCheck, TruckIcon as Truck, CalendarXIcon as CalendarX, WrenchIcon as Wrench, EyeSlashIcon as EyeSlash, TicketIcon as TicketIcon, ClipboardTextIcon as ClipboardText, ArrowsClockwiseIcon as ArrowsClockwise } from "@phosphor-icons/react";
import { RIP_STATO_COLOR } from "@/lib/riparazioni";

function Kpi({ icon: Icon, label, value, hint }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {label}
        </span>
        <Icon size={20} weight="bold" className="text-primary" />
      </div>
      <div className="font-heading text-3xl font-bold mt-3 tabular-nums">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [excludeTarget, setExcludeTarget] = useState(null); // cliente selezionato
  const [excludeStep, setExcludeStep] = useState(1); // 1 = prima conferma, 2 = conferma finale
  const [excluding, setExcluding] = useState(false);

  const loadStats = () => api.get("/dashboard/stats").then(({ data }) => setStats(data));

  useEffect(() => {
    loadStats();
  }, []);

  const openExclude = (c) => { setExcludeTarget(c); setExcludeStep(1); };
  const closeExclude = () => { if (!excluding) { setExcludeTarget(null); setExcludeStep(1); } };
  const confirmExclude = async () => {
    if (excludeStep === 1) { setExcludeStep(2); return; }
    setExcluding(true);
    try {
      await api.post(`/dashboard/inactive-clients/${excludeTarget.id}/exclude`);
      toast.success(`"${excludeTarget.cliente_nome}" non sarà più mostrato tra i clienti non movimentati`);
      setExcludeTarget(null);
      setExcludeStep(1);
      await loadStats();
    } catch (e) {
      toast.error("Errore durante l'esclusione del cliente");
    } finally {
      setExcluding(false);
    }
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader title="Dashboard" subtitle="Panoramica operativa CRM e fatturazione" />

        {!stats ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-sm" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="kpi-grid">
              <Kpi
                icon={Users}
                label="Clienti attivi"
                value={stats.active_clients}
                hint={`${stats.total_clients} totali`}
              />
              <Kpi icon={FileText} label="Contratti" value={stats.total_contracts} />
              <Kpi icon={Printer} label="Dispositivi" value={stats.total_devices} />
              <Kpi
                icon={CurrencyEur}
                label="Ricavi ricorrenti / mese"
                value={euro(stats.mrr)}
                hint="MRR stimato"
              />
            </div>

            <Card className="rounded-sm border-border shadow-none p-6 mt-6" data-testid="today-interventi-card">
              <div className="flex items-center gap-2 mb-4">
                <CalendarCheck size={18} weight="bold" className="text-primary" />
                <h3 className="font-heading text-lg font-semibold">Interventi di oggi</h3>
                <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-sm bg-secondary text-muted-foreground" data-testid="today-interventi-count">{stats.interventi_oggi_count || 0} sessioni</span>
              </div>
              {(!stats.interventi_oggi_by_tecnico || stats.interventi_oggi_by_tecnico.length === 0) ? (
                <p className="text-sm text-muted-foreground py-4">Nessuna sessione di lavoro programmata per oggi.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stats.interventi_oggi_by_tecnico.map((t) => (
                    <div key={t.tecnico_id || "none"} className="rounded-sm border border-border p-3" data-testid="today-tecnico-col">
                      <div className="text-sm font-semibold mb-2 pb-2 border-b border-border flex items-center justify-between">
                        <span>{t.tecnico_nome}</span>
                        <span className="text-xs font-normal text-muted-foreground">{t.sessioni.length}</span>
                      </div>
                      <div className="space-y-1.5">
                        {t.sessioni.map((s, i) => (
                          <a key={i} href={`/interventi/${s.intervento_id}`} className="block rounded-sm bg-secondary/60 hover:bg-secondary px-2 py-1.5 transition-colors" data-testid="today-session-row">
                            <div className="text-xs font-semibold tabular-nums text-primary">{s.ora_inizio}{s.ora_fine ? `–${s.ora_fine}` : ""}</div>
                            <div className="text-sm font-medium truncate">{s.titolo}</div>
                            <div className="text-xs text-muted-foreground truncate">{s.cliente_nome}</div>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <TicketOverview data={stats.tickets_overview} />

            <RiparazioniOverview data={stats.riparazioni_overview} />

            <ContrattiAlert />

            <Scadenze30 />

            <ScadenzeGenerali />

            <Card className="rounded-sm border-border shadow-none p-6 mt-6" data-testid="inactive-clients-card">
              <div className="flex items-center gap-2 mb-4">
                <ClockCounterClockwise size={18} weight="bold" className="text-primary" />
                <h3 className="font-heading text-lg font-semibold">Clienti attivi non movimentati</h3>
                {stats.inactive_alert_count > 0 && (
                  <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-sm bg-red-100 text-red-700" data-testid="inactive-alert-count">
                    {stats.inactive_alert_count} oltre 12 mesi
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-3">Ultima azione registrata (appuntamento, intervento, fattura, ticket o riparazione). In rosso i clienti fermi da oltre 12 mesi o mai movimentati.</p>
              {(!stats.inactive_clients || stats.inactive_clients.length === 0) ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Nessun cliente attivo.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Ultima movimentazione</TableHead>
                      <TableHead className="text-right">Inattivo da</TableHead>
                      <TableHead className="text-right w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.inactive_clients.map((c) => (
                      <TableRow key={c.id} data-testid="inactive-client-row" className="hover:bg-secondary">
                        <TableCell className="font-medium">
                          <a href={`/clienti/${c.id}`} className="hover:text-primary hover:underline">{c.cliente_nome}</a>
                        </TableCell>
                        <TableCell className="tabular-nums text-muted-foreground">{c.ultima_movimentazione ? fmtDate(c.ultima_movimentazione) : "Mai"}</TableCell>
                        <TableCell className="text-right">
                          {c.alert ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-sm bg-red-100 text-red-700" data-testid="inactive-red-badge">
                              <Warning size={12} weight="fill" /> {c.mesi_inattivo != null ? `${c.mesi_inattivo} mesi` : "Mai movimentato"}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground tabular-nums">{c.mesi_inattivo != null ? `${c.mesi_inattivo} mesi` : "—"}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                            title="Escludi dalla visualizzazione"
                            onClick={() => openExclude(c)}
                            data-testid={`exclude-inactive-btn-${c.id}`}
                          >
                            <EyeSlash size={16} weight="bold" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>

            <Dialog open={!!excludeTarget} onOpenChange={(o) => { if (!o) closeExclude(); }}>
              <DialogContent data-testid="exclude-inactive-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-700">
                    <Warning size={20} weight="fill" /> {excludeStep === 1 ? "Escludere questo cliente?" : "Conferma definitiva"}
                  </DialogTitle>
                  <DialogDescription className="pt-2 text-sm leading-relaxed">
                    {excludeStep === 1 ? (
                      <>Stai per escludere <span className="font-semibold text-foreground">{excludeTarget?.cliente_nome}</span> dall'elenco dei clienti non movimentati. Questo cliente <span className="font-semibold text-red-700">non sarà mai più mostrato</span> in questa sezione della dashboard.</>
                    ) : (
                      <>Sei sicuro? L'operazione è definitiva: <span className="font-semibold text-foreground">{excludeTarget?.cliente_nome}</span> non comparirà <span className="font-semibold text-red-700">mai più</span> tra i clienti non movimentati. Confermi?</>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={closeExclude} disabled={excluding} data-testid="exclude-cancel-btn">Annulla</Button>
                  <Button
                    className={excludeStep === 2 ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                    variant={excludeStep === 2 ? "default" : "destructive"}
                    onClick={confirmExclude}
                    disabled={excluding}
                    data-testid="exclude-confirm-btn"
                  >
                    {excluding ? "Esclusione…" : excludeStep === 1 ? "Continua" : "Escludi definitivamente"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>


            {stats.suspended_count > 0 && (
              <Card className="rounded-sm border-red-300 shadow-none p-6 mt-6" data-testid="suspended-clients-card">
                <div className="flex items-center gap-2 mb-4">
                  <Lock size={18} weight="bold" className="text-red-600" />
                  <h3 className="font-heading text-lg font-semibold">Clienti sospesi per insolvenza</h3>
                  <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-sm bg-red-100 text-red-700" data-testid="suspended-count">{stats.suspended_count}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Per questi clienti non è possibile creare interventi e i ticket sono sospesi (non evadibili) fino alla riattivazione.</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead className="text-right">Sospeso il</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.suspended_clients.map((c) => (
                      <TableRow key={c.id} data-testid="suspended-client-row" className="hover:bg-secondary">
                        <TableCell className="font-medium"><a href={`/clienti/${c.id}`} className="hover:text-primary hover:underline">{c.cliente_nome}</a></TableCell>
                        <TableCell className="text-muted-foreground">{c.motivo || "Insolvenza"}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{c.sospeso_il ? fmtDate(c.sospeso_il) : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </>
        )}
      </div>
    </InternalLayout>
  );
}

function TicketOverview({ data }) {
  if (!data) return null;
  const stat = (label, value, tone) => (
    <div className="rounded-sm border border-border p-3" data-testid={`tk-ov-${label}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
  const attiviRaw = (data.per_stato || []).filter((s) => !s.finale && s.count > 0);
  const attivi = (() => {
    const out = [];
    let attesa = null;
    for (const s of attiviRaw) {
      if ((s.stato || "").toLowerCase().startsWith("in attesa")) {
        if (!attesa) { attesa = { stato: "In attesa", count: 0, colore: s.colore, ids: [] }; out.push(attesa); }
        attesa.count += s.count;
        if (s.stato_id) attesa.ids.push(s.stato_id);
      } else {
        out.push({ ...s, ids: s.stato_id ? [s.stato_id] : [] });
      }
    }
    return out;
  })();
  return (
    <Card className="rounded-sm border-border shadow-none p-6 mt-6" data-testid="tickets-overview-card">
      <div className="flex items-center gap-2 mb-4">
        <TicketIcon size={18} weight="bold" className="text-primary" />
        <h3 className="font-heading text-lg font-semibold">Ticket</h3>
        <a href="/helpdesk/ticket" className="ml-auto text-xs text-primary hover:underline" data-testid="tk-ov-link">Vai ai ticket →</a>
      </div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Ticket aperti per stato</div>
      {attivi.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2 mb-5">Nessun ticket aperto.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
          {attivi.map((s) => (
            <a key={s.stato} href={s.ids && s.ids.length ? `/helpdesk/ticket?stato_id=${s.ids.join(",")}` : "/helpdesk/ticket"} className="block rounded-sm border border-border p-4 hover:border-primary/40 transition-colors" data-testid="tk-ov-stato">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.colore }} />
                {s.stato}
              </span>
              <div className="font-heading text-3xl font-bold mt-2 tabular-nums">{s.count}</div>
            </a>
          ))}
        </div>
      )}
      {(data.sla_scaduti > 0 || data.sla_in_scadenza > 0) ? (
        <div className="grid grid-cols-2 gap-3">
          <a href="/helpdesk/ticket?sla_status=scaduto" className="rounded-sm border border-rose-200 bg-rose-50 p-3 hover:border-rose-400 transition-colors" data-testid="tk-ov-sla-scaduti">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-rose-700 font-semibold"><Warning size={13} weight="fill" /> SLA scaduti</div>
            <div className="font-heading text-2xl font-bold mt-1 tabular-nums text-rose-700">{data.sla_scaduti || 0}</div>
          </a>
          <a href="/helpdesk/ticket?sla_status=in_scadenza" className="rounded-sm border border-amber-200 bg-amber-50 p-3 hover:border-amber-400 transition-colors" data-testid="tk-ov-sla-scadenza">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-amber-700 font-semibold"><ClockCounterClockwise size={13} weight="bold" /> SLA in scadenza</div>
            <div className="font-heading text-2xl font-bold mt-1 tabular-nums text-amber-700">{data.sla_in_scadenza || 0}</div>
          </a>
        </div>
      ) : null}
    </Card>
  );
}

const RIP_FASI_DASH = ["Ritirato", "In diagnosi", "In lavorazione", "In attesa cliente", "In attesa ricambio", "In attesa lavorazione est.", "Pronto per riconsegna"];

function RiparazioniOverview({ data }) {
  if (!data) return null;
  return (
    <Card className="rounded-sm border-border shadow-none p-6 mt-6" data-testid="riparazioni-overview-card">
      <div className="flex items-center gap-2 mb-4">
        <Wrench size={18} weight="bold" className="text-primary" />
        <h3 className="font-heading text-lg font-semibold">Riparazioni</h3>
        <a href="/riparazioni" className="ml-auto text-xs text-primary hover:underline" data-testid="rip-ov-link">Vai alle riparazioni →</a>
      </div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Fasi delle riparazioni aperte</div>
      {data.aperte === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Nessuna riparazione aperta.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
          {(data.fasi || []).filter((f) => RIP_FASI_DASH.includes(f.stato)).map((f) => (
            <a key={f.stato} href="/riparazioni" className="block rounded-sm border border-border p-2.5 hover:border-primary/40 transition-colors" data-testid="rip-ov-fase">
              <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-sm border ${RIP_STATO_COLOR[f.stato] || ""}`}>{f.stato}</span>
              <div className="font-heading text-xl font-bold mt-1.5 tabular-nums">{f.count}</div>
            </a>
          ))}
        </div>
      )}
    </Card>
  );
}

function ScadenzeGenerali() {
  const [data, setData] = useState(null);
  const [automezzi, setAutomezzi] = useState([]);
  useEffect(() => { api.get("/dashboard/scadenze-generali").then(({ data }) => setData(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/dashboard/scadenze-30").then(({ data }) => {
    const cat = (data.categorie || []).find((c) => c.nome === "Automezzi");
    setAutomezzi(cat ? cat.voci : []);
  }).catch(() => {}); }, []);
  if (!data) return null;
  const PRIO_BADGE = { critica: "bg-rose-600 text-white", alta: "bg-orange-100 text-orange-700", media: "bg-blue-100 text-blue-700", bassa: "bg-slate-100 text-slate-600" };
  const badge = (v) => v.scaduta
    ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700">Scaduta da {Math.abs(v.giorni)}g</span>
    : (v.giorni === 0 ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-800">Oggi</span>
      : (v.giorni != null && v.giorni <= 7 ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">Tra {v.giorni}g</span>
        : <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">{v.giorni != null ? `Tra ${v.giorni}g` : "—"}</span>));
  return (
    <Card className="rounded-sm border-border shadow-none p-6 mt-6" data-testid="scadenze-generali-card">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardText size={18} weight="bold" className="text-primary" />
        <h3 className="font-heading text-lg font-semibold">Scadenziario</h3>
        <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-sm bg-secondary text-muted-foreground" data-testid="scadenze-generali-total">{data.totale}</span>
        {data.scadute > 0 && <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-red-100 text-red-700" data-testid="scadenze-generali-scadute">{data.scadute} scadute</span>}
      </div>
      {(() => {
      const voci = (data.voci || []).filter((v) => v.scaduta || (v.giorni != null && v.giorni <= 30));
      const clienti = voci.filter((v) => v.gruppo === "clienti");
      const tecnologie = voci.filter((v) => v.gruppo !== "clienti");
      const row = (v, i) => (
        <a key={i} href={v.link || "#"} className="flex items-center justify-between gap-2 rounded-sm bg-secondary/50 hover:bg-secondary px-3 py-2 transition-colors cursor-pointer" data-testid="scadenza-generale-row">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-1.5">
              {v.priorita && <span className={`text-[10px] font-semibold px-1 py-0.5 rounded-sm ${PRIO_BADGE[v.priorita] || "bg-slate-100 text-slate-600"}`}>{v.priorita}</span>}
              {v.label}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">{v.sub}{v.sub ? " · " : ""}{fmtDate(v.data)}</div>
          </div>
          <div className="shrink-0">{badge(v)}</div>
        </a>
      );
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div data-testid="scadenze-generali-col-tecnologie">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Tecnologie</div>
            <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto">
              {tecnologie.length === 0 ? <p className="text-sm text-muted-foreground py-2">Nessuna scadenza.</p> : tecnologie.map(row)}
            </div>
          </div>
          <div data-testid="scadenze-generali-col-clienti">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Clienti</div>
            <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto">
              {clienti.length === 0 ? <p className="text-sm text-muted-foreground py-2">Nessuna scadenza.</p> : clienti.map(row)}
            </div>
          </div>
          <div data-testid="scadenze-generali-col-automezzi">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Automezzi</div>
            <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto">
              {automezzi.length === 0 ? <p className="text-sm text-muted-foreground py-2">Nessuna scadenza.</p> : automezzi.map(row)}
            </div>
          </div>
        </div>
      );
      })()}
    </Card>
  );
}

function Scadenze30() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/scadenze-30").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  const CAT_ICON = { "Contratti": FileText, "Servizi & Domini": Globe, "Automezzi": Truck };
  const badge = (v) => v.scaduta
    ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700">Scaduta</span>
    : (v.giorni <= 7 ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">Tra {v.giorni}g</span>
      : <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">Tra {v.giorni}g</span>);
  const byName = (n) => (data.categorie || []).find((c) => c.nome === n) || { nome: n, count: 0, voci: [] };
  const catCard = (cat, title, subtitle, testid) => {
    const Icon = CAT_ICON[cat.nome] || Globe;
    return (
      <Card className="rounded-sm border-border shadow-none p-6 mt-6" data-testid={testid}>
        <div className="flex items-center gap-2 mb-1">
          <Icon size={18} weight="bold" className="text-primary" />
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-sm bg-secondary text-muted-foreground">{cat.count}</span>
        </div>
        {cat.voci.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Nessuna scadenza.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {cat.voci.map((v, i) => (
              <a key={i} href={v.link || "#"} className="flex items-center justify-between gap-2 rounded-sm bg-secondary/50 hover:bg-secondary px-3 py-2 transition-colors cursor-pointer" data-testid="scadenza-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{v.label}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{v.sub} · {fmtDate(v.data)}</div>
                </div>
                <div className="shrink-0">{badge(v)}</div>
              </a>
            ))}
          </div>
        )}
      </Card>
    );
  };
  return catCard(byName("Servizi & Domini"), "Scadenze servizi & domini", "Servizi e domini in scadenza nei prossimi 30 giorni (incluse quelle appena scadute).", "scadenze-30-card");
}

function ContrattiAlert() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/contratti-alert").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  const total = (data.da_rinnovare?.length || 0) + (data.poche_ore?.length || 0) + (data.negativo?.length || 0);
  const link = (c) => c.cliente_id ? `/clienti/${c.cliente_id}` : "/contratti";
  const col = (titolo, voci, render, testid) => (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
        {titolo}
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-secondary text-muted-foreground">{voci.length}</span>
      </div>
      <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto">
        {voci.length === 0 ? <p className="text-sm text-muted-foreground py-2">Nessun contratto.</p> : voci.map(render)}
      </div>
    </div>
  );
  const rowBase = "flex items-center justify-between gap-2 rounded-sm bg-secondary/50 hover:bg-secondary px-3 py-2 transition-colors cursor-pointer";
  return (
    <Card className="rounded-sm border-border shadow-none p-6 mt-6" data-testid="scadenze-contratti-card">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} weight="bold" className="text-primary" />
        <h3 className="font-heading text-lg font-semibold">Contratti da gestire</h3>
        <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-sm bg-secondary text-muted-foreground" data-testid="contratti-alert-total">{total}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {col("Da rinnovare", data.da_rinnovare || [], (c, i) => (
          <a key={i} href={link(c)} className={rowBase} data-testid="contratto-rinnovo-row">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.nome}</div>
              <div className="text-[11px] text-muted-foreground truncate">{c.cliente_nome}{c.data_scadenza ? ` · ${fmtDate(c.data_scadenza)}` : ""}</div>
            </div>
            <span className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">Da rinnovare</span>
          </a>
        ), "contratti-col-rinnovare")}
        {col("Poche ore residue", data.poche_ore || [], (c, i) => (
          <a key={i} href={link(c)} className={rowBase} data-testid="contratto-poche-ore-row">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.nome}</div>
              <div className="text-[11px] text-muted-foreground truncate">{c.cliente_nome}</div>
            </div>
            <span className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-700">{c.ore_residue}h</span>
          </a>
        ), "contratti-col-poche-ore")}
        {col("Monte ore negativo", data.negativo || [], (c, i) => (
          <a key={i} href={link(c)} className={rowBase} data-testid="contratto-negativo-row">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.nome}</div>
              <div className="text-[11px] text-muted-foreground truncate">{c.cliente_nome}</div>
            </div>
            <span className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700">{c.ore_residue}h</span>
          </a>
        ), "contratti-col-negativo")}
      </div>
    </Card>
  );
}
